import { Direction } from '../types';
import { ROUTES } from './constants';
import { getChileNow } from './date';

/**
 * Resultado de una validación
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Valida que una fecha esté en formato YYYY-MM-DD y no sea en el pasado
 * 
 * @param dateStr - Fecha a validar en formato YYYY-MM-DD
 * @returns Resultado de la validación
 */
export const validateTripDate = (dateStr: string): ValidationResult => {
  if (!dateStr || dateStr.trim() === '') {
    return { isValid: false, error: 'La fecha es requerida' };
  }

  // Validar formato YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) {
    return { isValid: false, error: 'Formato de fecha inválido' };
  }

  const date = new Date(dateStr + 'T00:00:00');
  
  // Verificar que sea una fecha válida
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Fecha inválida' };
  }

  // Verificar que no sea en el pasado (permite hoy, usando fecha Chile)
  const chileToday = getChileNow().date;
  if (dateStr < chileToday) {
    return { isValid: false, error: 'La fecha no puede ser en el pasado' };
  }

  return { isValid: true };
};

/**
 * Valida que una hora de salida esté en el rango válido (0-23.5)
 * 
 * @param departureTime - Hora de salida (0-23.5)
 * @returns Resultado de la validación
 */
export const validateDepartureTime = (departureTime: number): ValidationResult => {
  if (typeof departureTime !== 'number' || isNaN(departureTime)) {
    return { isValid: false, error: 'La hora de salida debe ser un número' };
  }

  if (departureTime < 0 || departureTime > 23.5) {
    return { isValid: false, error: 'La hora de salida debe estar entre 0 y 23:30' };
  }

  return { isValid: true };
};

/**
 * Valida que un ID de ruta sea válido
 * 
 * @param routeId - ID de la ruta
 * @returns Resultado de la validación
 */
export const validateRouteId = (routeId: string): ValidationResult => {
  if (!routeId || routeId.trim() === '') {
    return { isValid: false, error: 'La ruta es requerida' };
  }

  const routeExists = ROUTES.some(route => route.id === routeId);
  if (!routeExists) {
    return { isValid: false, error: 'Ruta no válida' };
  }

  return { isValid: true };
};

/**
 * Valida que una dirección sea válida
 * 
 * @param direction - Dirección del viaje
 * @returns Resultado de la validación
 */
export const validateDirection = (direction: Direction): ValidationResult => {
  if (!direction) {
    return { isValid: false, error: 'La dirección es requerida' };
  }

  const validDirections = Object.values(Direction);
  if (!validDirections.includes(direction)) {
    return { isValid: false, error: 'Dirección no válida' };
  }

  return { isValid: true };
};

/**
 * Valida todos los datos de un viaje antes de guardarlo
 * 
 * @param tripDate - Fecha del viaje
 * @param departureTime - Hora de salida
 * @param routeId - ID de la ruta
 * @param direction - Dirección del viaje
 * @returns Resultado de la validación con el primer error encontrado
 */
export const validateTrip = (
  tripDate: string,
  departureTime: number,
  routeId: string,
  direction: Direction
): ValidationResult => {
  // Validar fecha
  const dateValidation = validateTripDate(tripDate);
  if (!dateValidation.isValid) {
    return dateValidation;
  }

  // Validar hora de salida
  const timeValidation = validateDepartureTime(departureTime);
  if (!timeValidation.isValid) {
    return timeValidation;
  }

  // Validar que fecha+hora no sea en el pasado (usando hora Chile, no local del browser).
  // Un slot (30 min) se considera pasado solo cuando el bloque completo ha transcurrido:
  // departureTime=14.5 (14:30) sigue siendo válido hasta las 15:00, no desde las 14:31.
  const chile = getChileNow();
  if (tripDate === chile.date) {
    const currentDecimal = chile.hours + chile.minutes / 60;
    if (departureTime + 0.5 <= currentDecimal) {
      return { isValid: false, error: 'La hora de salida no puede ser en el pasado' };
    }
  }

  // Validar ruta
  const routeValidation = validateRouteId(routeId);
  if (!routeValidation.isValid) {
    return routeValidation;
  }

  // Validar dirección
  const directionValidation = validateDirection(direction);
  if (!directionValidation.isValid) {
    return directionValidation;
  }

  return { isValid: true };
};
